import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { executeCellTypingMarkerPositivityOrchestration } from 'api/platform';
import {
  getUndoneIncludeExcludeAnnotations,
  isAnalysisAreaAssignment,
} from 'components/StudyDashboard/Workflow/analysisAreas';
import { AnalysisAreasReadinessNotice } from 'components/StudyDashboard/Workflow/AnalysisAreasReadinessNotice';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { CellRuleValue } from 'interfaces/cellRule';
import {
  CellTypingExecutionUnitId,
  PanelOrchestrationExecuteResponse,
} from 'interfaces/jobs/cellTypingMarkerPositivity';
import { MultiplexPanelChannel, MultiplexPanelView } from 'interfaces/multiplexPanels';
import React, { useEffect, useState } from 'react';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import type { CellTypingRequestedOutputs } from '.';
import {
  estimatesMifCutoffsInRun,
  executionChoicesForPanel,
  orchestrationRequestForPanel,
  orchestrationSubmitBlockReason,
} from './executionUnits';
import { PanelConfigurationDraft } from './PanelConfigurationEditor';

interface CellTypeOption {
  id: string;
  displayName: string;
  isAbstract?: boolean;
}

interface Props {
  studyId?: string;
  labId?: string;
  panels: MultiplexPanelView[];
  drafts: Record<string, PanelConfigurationDraft>;
  selectedSlideIds: string[];
  requestedOutputsByPanel: Record<string, CellTypingRequestedOutputs>;
  cellTypes?: CellTypeOption[];
  renderPanel?: (
    panel: MultiplexPanelView,
    runAction: React.ReactNode,
    settings: React.ReactNode,
    activePanel: MultiplexPanelView,
    activeUnits: CellTypingExecutionUnitId[]
  ) => React.ReactNode;
  onCellTypesChange?: (panelId: string, enabled: boolean) => void;
  onExecutionDirtyChange?: (dirty: boolean) => void;
}

const channelLabel = (channel: MultiplexPanelChannel) =>
  channel.displayName?.trim() || channel.identifiers.sourceNames.filter(Boolean).join(', ') || 'Unnamed channel';

const modalityLabel = (channel: MultiplexPanelChannel) => {
  if (channel.channelModality === 'rna') return 'RNA';
  if (channel.channelModality === 'dna') return 'DNA';
  return 'mIF';
};
const executionErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error && 'response' in error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) =>
          typeof item === 'object' && item && 'msg' in item ? String((item as { msg: unknown }).msg) : String(item)
        )
        .join(' ');
    }
  }
  return error instanceof Error ? error.message : 'The analysis platform did not accept this run.';
};

export const PanelExecutionUnits = ({
  studyId,
  labId,
  panels,
  drafts,
  selectedSlideIds,
  requestedOutputsByPanel,
  renderPanel,
  onCellTypesChange,
  onExecutionDirtyChange,
}: Props) => {
  const [unitSelections, setUnitSelections] = useState<
    Record<string, Partial<Record<CellTypingExecutionUnitId, boolean>>>
  >({});
  const [includedChannels, setIncludedChannels] = useState<Record<string, string[]>>({});
  const [expandedMifPanels, setExpandedMifPanels] = useState<Record<string, boolean>>({});
  const [thresholdSourceByPanel, setThresholdSourceByPanel] = useState<Record<string, 'saved' | 'estimate'>>({});
  const [result, setResult] = useState<PanelOrchestrationExecuteResponse | null>(null);
  const [useAnalysisAreas, setUseAnalysisAreas] = useState(true);
  const assignmentsQuery = useAnnotationAssignments(studyId || '', false, true, !studyId);
  const activeAnalysisAreaAssignments = (assignmentsQuery.data?.annotationAssignments || []).filter(
    (assignment) => isAnalysisAreaAssignment(assignment) && !assignment.archived
  );
  const canUseAnalysisAreas =
    !assignmentsQuery.isLoading && !assignmentsQuery.isError && activeAnalysisAreaAssignments.length === 1;
  const applyAnalysisAreas = canUseAnalysisAreas && useAnalysisAreas;
  const analysisAreasHelper = assignmentsQuery.isLoading
    ? 'Checking analysis-area assignments…'
    : assignmentsQuery.isError
    ? 'Analysis-area assignments could not be loaded.'
    : activeAnalysisAreaAssignments.length === 0
    ? 'Create an analysis-area assignment before using drawn areas.'
    : activeAnalysisAreaAssignments.length > 1
    ? 'More than one analysis-area assignment exists. The run will not choose one.'
    : applyAnalysisAreas
    ? 'Draw Include within the tissue mask to limit the analysis area. Without Include, the tissue mask is used. Exclude areas are then removed. Cells outside the resulting area are removed.'
    : 'Drawn areas are not applied to this run.';
  const [error, setError] = useState('');
  useEffect(() => {
    const excludedChannels = panels.some((panel) => {
      const available = panel.channels.flatMap((channel) => (channel.saved ? [channel.saved.analysisName] : []));
      const included = includedChannels[panel.panelId] ?? available;
      return included.length !== available.length || available.some((channelId) => !included.includes(channelId));
    });
    const excludedSteps = Object.values(unitSelections).some((selections) =>
      Object.values(selections).some((selected) => selected === false)
    );
    const changedCutoffSource = Object.values(thresholdSourceByPanel).some((value) => value !== 'saved');
    const declinedAnalysisAreas = canUseAnalysisAreas && !useAnalysisAreas;
    onExecutionDirtyChange?.(excludedChannels || excludedSteps || changedCutoffSource || declinedAnalysisAreas);
  }, [
    canUseAnalysisAreas,
    includedChannels,
    onExecutionDirtyChange,
    panels,
    thresholdSourceByPanel,
    unitSelections,
    useAnalysisAreas,
  ]);
  const mutation = useMutation({
    mutationFn: executeCellTypingMarkerPositivityOrchestration,
    onSuccess: (response) => {
      setResult(response);
      setError('');
    },
    onError: (executionError: unknown) => {
      setResult(null);
      setError(executionErrorMessage(executionError));
    },
  });

  const runUnits = (panel: MultiplexPanelView, unitIds: CellTypingExecutionUnitId[]) => {
    const draft = drafts[panel.panelId];
    const requestedOutputs = requestedOutputsByPanel[panel.panelId];
    const targetSlideIds = panel.slides
      .filter((slide) => selectedSlideIds.includes(slide.slideId))
      .map((slide) => slide.slideId);
    if (!studyId || !labId || !draft) {
      setError('Study, lab, and panel settings are required before submitting this run.');
      return;
    }
    const channels = panel.channels.flatMap((channel) => (channel.saved ? [channel.saved] : []));
    const includedIds = includedChannels[panel.panelId] ?? channels.map((channel) => channel.analysisName);
    const requestInput = {
      panel,
      draft,
      targetSlideIds,
      useAnalysisAreas: applyAnalysisAreas,
      requestedOutputs,
      executionUnits: unitIds,
      includedMifMarkerIds: channels
        .filter((channel) => channel.channelModality === 'mif' && includedIds.includes(channel.analysisName))
        .map((channel) => channel.analysisName),
      includedRnaProbeIds: channels
        .filter((channel) => channel.channelModality === 'rna' && includedIds.includes(channel.analysisName))
        .map((channel) => channel.analysisName),
      thresholdSource: thresholdSourceByPanel[panel.panelId] ?? ('saved' as const),
    };
    const panelRequest = orchestrationRequestForPanel(requestInput);
    if (!panelRequest) {
      setResult(null);
      setError(orchestrationSubmitBlockReason(requestInput) ?? 'This panel cannot be submitted yet.');
      return;
    }
    setError('');
    mutation.mutate({ studyId, labId, panel: panelRequest });
  };

  return (
    <Stack spacing={2} aria-label="Panel execution">
      <FormControlLabel
        label="Use Include and Exclude areas"
        control={
          <Checkbox
            checked={applyAnalysisAreas}
            disabled={!canUseAnalysisAreas || mutation.isLoading}
            onChange={(_event, isChecked) => setUseAnalysisAreas(isChecked)}
          />
        }
      />
      <Typography variant="body2" color="text.secondary">
        {analysisAreasHelper}
      </Typography>
      {studyId && canUseAnalysisAreas && getUndoneIncludeExcludeAnnotations(activeAnalysisAreaAssignments[0]).length ? (
        <AnalysisAreasReadinessNotice
          assignment={activeAnalysisAreaAssignments[0]}
          studyId={studyId}
          labId={labId}
          doneLabel="Set all as done"
        />
      ) : null}
      {panels.map((panel) => {
        const requestedOutputs = requestedOutputsByPanel[panel.panelId];
        const targetSlides = panel.slides.filter((slide) => selectedSlideIds.includes(slide.slideId));
        if (!targetSlides.length && !renderPanel) return null;
        const draft = drafts[panel.panelId];
        const channels = panel.channels.flatMap((channel) => (channel.saved ? [channel.saved] : []));
        const includedIds = includedChannels[panel.panelId] ?? channels.map((channel) => channel.analysisName);
        const omittedCount = channels.length - includedIds.length;
        const thresholdSource = thresholdSourceByPanel[panel.panelId] ?? 'saved';
        const includedMifMarkerIds = channels
          .filter((channel) => channel.channelModality === 'mif' && includedIds.includes(channel.analysisName))
          .map((channel) => channel.analysisName);
        const includedRnaProbeIds = channels
          .filter((channel) => channel.channelModality === 'rna' && includedIds.includes(channel.analysisName))
          .map((channel) => channel.analysisName);
        const constrainedMifMarkerIds = draft
          ? [
              ...new Set(
                draft.cellRules
                  .filter(
                    (rule) =>
                      includedMifMarkerIds.includes(rule.stainTypeId) && rule.ruleValue !== CellRuleValue.Indifferent
                  )
                  .map((rule) => rule.stainTypeId)
              ),
            ]
          : [];
        const estimatesCutoffs = Boolean(
          draft && estimatesMifCutoffsInRun(thresholdSource, draft.markerThresholds, constrainedMifMarkerIds)
        );
        const activePanel = {
          ...panel,
          channels: panel.channels.filter(
            (channel) => !channel.saved || includedIds.includes(channel.saved.analysisName)
          ),
        };
        const choices = executionChoicesForPanel({
          panel,
          draft,
          requestedOutputs,
          includedMifMarkerIds,
          includedRnaProbeIds,
          thresholdSource,
        });
        const activeUnits = choices
          .filter((choice) =>
            choice.id === 'cell_typing' && onCellTypesChange
              ? requestedOutputs.cellTypes
              : unitSelections[panel.panelId]?.[choice.id] !== false
          )
          .map((choice) => choice.id);
        const checked = choices
          .filter((choice) => choice.applicable && activeUnits.includes(choice.id))
          .map((choice) => choice.id);
        const runnable = choices.filter((choice) => choice.applicable && checked.includes(choice.id));
        const unitHasChannels = (unitId: CellTypingExecutionUnitId) =>
          unitId === 'calculate_features' ? includedRnaProbeIds.length > 0 : includedMifMarkerIds.length > 0;
        const submitBlockReason =
          draft && runnable.length
            ? orchestrationSubmitBlockReason({
                panel,
                draft,
                targetSlideIds: targetSlides.map((slide) => slide.slideId),
                requestedOutputs,
                executionUnits: runnable.map((choice) => choice.id),
                includedMifMarkerIds,
                includedRnaProbeIds,
                thresholdSource,
              })
            : !panel.revision
            ? 'Save the panel definition before running it.'
            : !runnable.length
            ? choices.find((choice) => activeUnits.includes(choice.id) && unitHasChannels(choice.id))?.reason ??
              'Select a step before running this panel.'
            : 'Study, lab, and panel settings are required before submitting this run.';
        const canRun = Boolean(!submitBlockReason && !mutation.isLoading);
        const isSubmittedPanel = mutation.variables?.panel.panelId === panel.panelId;
        const runAction = (
          <Stack direction="row" spacing={1.5} alignItems="center">
            {isSubmittedPanel && !mutation.isLoading && (error || result) && (
              <Alert severity={error ? 'error' : 'success'} sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                {error || `Orchestration submitted. Job ${result!.jobId}. The analysis platform is starting it.`}
              </Alert>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={
                mutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlinedIcon />
              }
              disabled={!canRun}
              onClick={() =>
                runUnits(
                  panel,
                  runnable.map((choice) => choice.id)
                )
              }
              sx={{ flexShrink: 0 }}
            >
              {mutation.isLoading ? 'Submitting…' : 'Run this panel'}
            </Button>
          </Stack>
        );
        const renderChoice = (choice: ReturnType<typeof executionChoicesForPanel>[number]) => (
          <Stack key={choice.id} component="li" direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              label={choice.label}
              control={
                <Checkbox
                  checked={unitHasChannels(choice.id) && activeUnits.includes(choice.id)}
                  disabled={mutation.isLoading || !unitHasChannels(choice.id)}
                  onChange={(_event, isChecked) => {
                    if (choice.id === 'cell_typing' && onCellTypesChange) onCellTypesChange(panel.panelId, isChecked);
                    else
                      setUnitSelections((current) => ({
                        ...current,
                        [panel.panelId]: { ...current[panel.panelId], [choice.id]: isChecked },
                      }));
                  }}
                />
              }
            />
            <Typography variant="body2" color="text.secondary">
              {choice.reason}
            </Typography>
            <Button
              size="small"
              variant="text"
              color="inherit"
              aria-label={`Run ${choice.label} only`}
              disabled={!canRun || !choice.applicable || !checked.includes(choice.id)}
              onClick={() => runUnits(panel, [choice.id])}
            >
              Run this step
            </Button>
          </Stack>
        );
        const settings = (
          <Stack spacing={1.5}>
            {!panel.revision && <Alert severity="info">Save the panel definition before running it.</Alert>}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {channels.map((channel) => (
                <FormControlLabel
                  key={channel.analysisName}
                  label={`${channelLabel(channel)} · ${modalityLabel(channel)}`}
                  control={
                    <Checkbox
                      checked={includedIds.includes(channel.analysisName)}
                      disabled={mutation.isLoading}
                      onChange={(_event, isChecked) => {
                        const next = {
                          ...includedChannels,
                          [panel.panelId]: isChecked
                            ? [...includedIds, channel.analysisName]
                            : includedIds.filter((channelId) => channelId !== channel.analysisName),
                        };
                        setIncludedChannels(next);
                      }}
                    />
                  }
                />
              ))}
            </Stack>
            {omittedCount > 0 && (
              <Alert severity="warning">
                {omittedCount === 1 ? '1 channel is' : `${omittedCount} channels are`} not included. Leave a channel out
                only when it did not pass QC.
              </Alert>
            )}
            <Accordion
              disableGutters
              disabled={!includedMifMarkerIds.length}
              expanded={Boolean(includedMifMarkerIds.length && (expandedMifPanels[panel.panelId] ?? true))}
              onChange={(_event, expanded) =>
                setExpandedMifPanels((current) => ({ ...current, [panel.panelId]: expanded }))
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '&.Mui-disabled': { opacity: 1 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ScienceOutlinedIcon color="action" />
                  <Typography variant="h3" component="h3">
                    mIF steps
                  </Typography>
                  {!includedMifMarkerIds.length && <Chip size="small" label="Not applicable" />}
                </Stack>
              </AccordionSummary>
              {includedMifMarkerIds.length > 0 && (
                <AccordionDetails>
                  <RadioGroup
                    row
                    aria-label="Cutoff source"
                    value={thresholdSource}
                    onChange={(event) =>
                      setThresholdSourceByPanel((current) => ({
                        ...current,
                        [panel.panelId]: event.target.value as 'saved' | 'estimate',
                      }))
                    }
                  >
                    <FormControlLabel value="saved" control={<Radio />} label="Use the cutoffs in this panel" />
                    <FormControlLabel value="estimate" control={<Radio />} label="Estimate cutoffs first" />
                  </RadioGroup>
                  {estimatesCutoffs && (
                    <Typography variant="body2" color="text.secondary">
                      This run estimates cutoffs for the included mIF channels from the binary classifier, then uses
                      those cutoffs for cell typing.
                    </Typography>
                  )}
                  <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
                    {choices.filter((choice) => choice.id !== 'calculate_features').map(renderChoice)}
                  </Stack>
                </AccordionDetails>
              )}
            </Accordion>
            <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
              {choices.filter((choice) => choice.id === 'calculate_features').map(renderChoice)}
            </Stack>
            {submitBlockReason &&
              submitBlockReason !== 'Save the panel definition before running it.' &&
              !mutation.isLoading && (
                <Typography variant="body2" color="text.secondary">
                  {submitBlockReason}
                </Typography>
              )}
            {mutation.isLoading && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Submitting this panel to the analysis platform.
              </Alert>
            )}
          </Stack>
        );
        if (renderPanel)
          return (
            <React.Fragment key={panel.panelId}>
              {renderPanel(panel, runAction, settings, activePanel, activeUnits)}
            </React.Fragment>
          );
        return (
          <Stack key={panel.panelId} spacing={1.5} aria-label={`${panel.name || 'Panel'} run`}>
            <Stack
              role="toolbar"
              aria-label={`${panel.name || 'Panel'} execution controls`}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={(theme) => ({
                ...getTranslucentSurfaceStyles(theme),
                position: 'sticky',
                top: 0,
                zIndex: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              })}
            >
              <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
                {panel.name || 'Panel'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {targetSlides.length} selected {targetSlides.length === 1 ? 'slide' : 'slides'}
              </Typography>
              {runAction}
            </Stack>
            {settings}
          </Stack>
        );
      })}
      {result?.warnings?.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}
      {result?.omittedOutputs.includes('hScores') && (
        <Alert severity="info">H-scores are produced with RNA counts, not with this step alone.</Alert>
      )}
      {error && !mutation.variables && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
};
